import {
  ContentSwitcher,
  SingleSelectDropdown,
  TextInput,
  Text,
  TruncatedText,
  type ListViewStatus,
} from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import {
  listBookmarks,
  listDatasets,
  unfiledFolderToken,
  type Dataset,
  type DatasetFolderTree,
  type PageResp,
} from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { DatasetPreview, resolveLoadErrorMessage } from "@/components/dataset";
import {
  flattenFolderTree,
  totalDatasetCount,
  useDatasetFolderTree,
} from "@/components/dataset/folder";
import { formatArmLayout } from "@/components/filter/datasetFilters";
import { formatNumber } from "@/utils";
import {
  MultiSelectList,
  type MultiSelectListMode,
} from "@/components/dataset/selection/MultiSelectList";
import type { DatasetSelection } from "./DatasetSelection";
import styles from "./DatasetSelection.module.css";
import type { DatasetStorageBackend } from "@/api/datasetStorageBackend";

const PAGE_SIZE = 30;

// 데이터셋 출처. 내가 담아 둔 목록(북마크)과 공용 Dataset 카탈로그 전체가 있다.
// 둘 다 같은 공용 Dataset 축이다.
type DatasetSource = "bookmarks" | "all";

const ALL_SOURCES: readonly DatasetSource[] = ["bookmarks", "all"];
// environment 로 좁히면 고를 수 있는 출처는 Dataset 하나뿐이다. 아래 주석을 참고한다.
const DATASET_ONLY_SOURCES: readonly DatasetSource[] = ["all"];

const SOURCE_OPTIONS = [
  { value: "bookmarks", label: "북마크" },
  { value: "all", label: "전체" },
];

// 출처마다 다른 것은 조회할 목록과 문구뿐이다. 검색, 페이지네이션, 선택은 같다.
// 두 출처 모두 Dataset 목록과 같은 검색 파라미터를 받는다. 북마크는 담긴 범위 안에서 좁힌다.
const SOURCE_COPY: Record<DatasetSource, { ariaLabel: string; emptyMessage: string }> = {
  bookmarks: {
    ariaLabel: "북마크에서 Dataset 선택",
    emptyMessage: "담아 둔 Dataset이 없습니다. `전체` 에서 골라 담아 보세요.",
  },
  all: { ariaLabel: "전체 Dataset 선택", emptyMessage: "Dataset이 없습니다." },
};

// 목록을 저장 위치로 좁힌 동안의 빈 문구. 좁혀서 빈 것일 수 있으므로 "없다"고 단정하지 않는다.
const NARROWED_EMPTY_MESSAGE: Record<DatasetSource, string> = {
  bookmarks: "담아 둔 Dataset 중 여기서 고를 수 있는 것이 없습니다. `전체` 에서 골라 보세요.",
  all: "여기서 고를 수 있는 Dataset이 없습니다.",
};

// 검색어가 있으면 빈 목록의 뜻이 다르다. "아직 없다"가 아니라 "이 검색어에 맞는 항목이 없다"이다.
const NO_MATCH_MESSAGE = "일치하는 항목이 없습니다. 다른 검색어를 입력해 보세요.";

// 검색 입력에서 요청까지의 디바운스. 한 글자마다 목록을 다시 조회하지 않는다.
const SEARCH_DEBOUNCE_MS = 300;

// 폴더 필터 없음 센티널. `?folder=` 를 보내지 않는다는 뜻이므로 `unfiled`(미분류만)와 다르다.
const ALL_FOLDERS_VALUE = "__all__";

/**
 * 폴더 드롭다운 옵션. 계층을 경로 라벨로 펼친다.
 *
 * c6 드롭다운 옵션 타입에는 group·depth 필드가 없다. 그래서 optgroup 이나 들여쓰기를 표현할 수
 * 없다. 루트는 `Picking`, 자식은 `Picking / 2026-Q1` 로 적는다. 이 표기는 목록의 `폴더` 컬럼과
 * 같다. 건수는 옵션의 `count` 슬롯에 넣는다.
 *
 * 트리의 건수와 목록이 다른 저장 위치 게이트를 쓰면 건수를 넣지 않는다. 모집단이 다른 수는
 * 표시하지 않는다(docs/rules.md §17).
 *
 * @param tree 폴더 트리 응답.
 * @param showCounts 건수를 넣을지. 트리의 건수와 목록이 같은 저장 위치 게이트를 쓸 때 true 다.
 * @returns `전체` · 폴더들 · `미분류` 순서의 옵션 목록.
 */
function folderOptions(tree: DatasetFolderTree, showCounts: boolean) {
  const options = [
    { value: ALL_FOLDERS_VALUE, label: "전체", count: totalDatasetCount(tree) },
    ...flattenFolderTree(tree).map((entry) => ({
      value: entry.uuid,
      label: entry.path,
      count: entry.datasetCount,
    })),
    { value: unfiledFolderToken, label: "미분류", count: tree.unfiled_count },
  ];
  return showCounts ? options : options.map(({ value, label }) => ({ value, label }));
}

function listPage(
  source: DatasetSource,
  query: {
    page: number;
    size: number;
    search?: string;
    folder?: string;
    environment?: string;
    storage_backend?: DatasetStorageBackend;
  },
): Promise<PageResp<Dataset>> {
  return source === "bookmarks" ? listBookmarks(query) : listDatasets(query);
}

type PickerItem = {
  uuid: string;
  name: string;
  // 대표 섬네일 presigned URL. 없거나(미생성, failed) 만료되면 이니셜을 표시한다.
  // 목록은 다시 조회하지 않는다. 다시 조회하면 무한 스크롤로 누적한 목록이 1페이지로 돌아간다.
  // 그것이 이니셜 표시보다 더 나쁘다.
  thumbnailUrl: string | null;
  // 이름 아래 요약 세 축(팔 구성 · 카메라 · 에피소드). 목록 응답만으로 만든다.
  meta: PickerItemMeta;
};

type PickerItemMeta = { arms: string; cameras: string; episodes: string };

// 잘림 툴팁이 있는 텍스트 변형. c6 는 Text variant 의 `as` 로 TruncatedText 를 넣는다.
// 래퍼로 감싸면 flex/grid 의 폭 제약 연결이 끊겨 잘림이 적용되지 않는다(RFC 017 §아키텍처).
const truncatedBody = { ...hatcheryTextModes["body-normal"], as: TruncatedText };
const truncatedCaption = { ...hatcheryTextModes["caption-normal"], as: TruncatedText };

// 제출 입력 데이터셋 picker. 출처를 전환하며 공용 Dataset 을 담는다. 선택은 호출부(스텝)가
// 소유한다(controlled). 담은 목록의 순서, role, 비율은 구성 스텝의 표가 다룬다.
//
// multi 에서 행 클릭과 키보드 이동은 커서(미리보기 대상, onPreview)만 옮긴다. 담기/해제는
// 체크박스 영역 클릭과 Space/Enter 로 한다. 담기 전에 내용을 확인하며 고를 수 있게 둘을 나눈다.
// single 은 체크박스가 없고 행 클릭이 곧 선택이다. MultiSelectList 가 두 모델을 설명한다.
// 출처를 바꿔도 선택과 검색어는 유지된다. 한 목록이 출처만 바꾸고 같은 selectedUuids 를 쓴다.
export function DatasetPicker({
  selectedUuids,
  onToggle,
  onPreview,
  selectionMode = "multi",
  countLabel,
  environment,
  storageBackend,
}: {
  selectedUuids: ReadonlySet<string>;
  onToggle: (selection: DatasetSelection) => void;
  onPreview?: (item: { uuid: string; name: string }) => void;
  selectionMode?: MultiSelectListMode;
  // 출처 세그먼트 우측에 표시하는 담은 수 문구. 담은 수를 아는 호출부가 문구를 만든다.
  countLabel?: string;
  // 예약 라벨 environment 로 목록을 좁힌다(스케줄 위저드의 simulation 전용). 지정하지 않으면 이 축은 없다.
  environment?: string;
  // 저장 위치로 목록을 좁힌다. 고른 Dataset 을 쓸 기능을 지원하는 위치만 남긴다. 고른 뒤에 거절되는
  // 후보를 만들지 않는다. 두 출처 모두 이 게이트를 받는다. 지정하지 않으면 이 축은 없다.
  storageBackend?: DatasetStorageBackend;
}): ReactNode {
  // 상류는 북마크 목록에서 `environment` 를 무시한다. 그래서 이 조건이 적용되면 북마크 출처를
  // **출처 목록에서 뺀다**. 시작 출처만 Dataset 으로 두면, 사용자가 북마크로 전환할 때 조건이
  // 경고 없이 해제된다.
  const sources = environment === undefined ? ALL_SOURCES : DATASET_ONLY_SOURCES;
  const [source, setSource] = useState<DatasetSource>(sources[0]);
  // 폴더는 출처와 독립이다. 북마크 목록도 같은 `?folder=` 축을 받으므로 출처를 바꿔도
  // 보던 폴더를 유지한다.
  const [folder, setFolder] = useState<string>(ALL_FOLDERS_VALUE);
  // 폴더 건수도 목록과 같은 저장 위치 게이트로 받는다. 그래야 옵션의 건수가 그 폴더로 거른 Dataset
  // 목록의 수와 같다.
  const { state: folderTreeState, countsCurrent: folderCountsCurrent } = useDatasetFolderTree(
    storageBackend ?? null,
  );
  const listProps: ListProps = {
    selectedUuids,
    onToggle,
    onPreview,
    selectionMode,
    environment,
    storageBackend,
  };
  return (
    <div className={styles.datasetPicker}>
      {/* 출처 전환과 담은 수. 목록의 출처와 지금까지 담은 개수를 한 줄에 둔다. */}
      <div className={styles.pickerSourceRow}>
        {sources.length > 1 ? (
          <ContentSwitcher
            label="Dataset 출처"
            size="sm"
            options={SOURCE_OPTIONS}
            value={source}
            onValueChange={(value) => setSource((value as DatasetSource | null) ?? sources[0])}
          />
        ) : null}
        {countLabel === undefined ? null : (
          <Text variant={hatcheryTextModes["caption-normal"]} className={styles.pickerCount}>
            {countLabel}
          </Text>
        )}
      </div>
      {folderTreeState.status === "success" && folderTreeState.data.folders.length > 0 ? (
        <div className={styles.pickerFolderRow}>
          <SingleSelectDropdown
            label="폴더"
            mode={hatcheryDropdownModes.filter}
            neutralValue={ALL_FOLDERS_VALUE}
            options={folderOptions(folderTreeState.data, folderCountsCurrent)}
            value={folder}
            onValueChange={setFolder}
            searchable
            fullWidth
          />
        </div>
      ) : null}
      <PickerList source={source} folder={folder} {...listProps} />
    </div>
  );
}

type ListProps = {
  selectedUuids: ReadonlySet<string>;
  onToggle: (selection: DatasetSelection) => void;
  onPreview?: (item: { uuid: string; name: string }) => void;
  selectionMode: MultiSelectListMode;
  environment?: string;
  storageBackend?: DatasetStorageBackend;
};

// 출처 하나를 검색과 무한 스크롤(누적)로 그린다. 검색창은 출처와 관계없이 항상 둔다.
// 북마크도 같은 검색 파라미터를 받는다(담긴 범위 안에서 좁힌다). 그래서 모든 출처에서 검색할 수 있다.
// 출처가 바뀌면 검색어를 유지하고 1페이지부터 다시 받는다. 응답 순서가 바뀌는 문제는 requestRef 가 막는다.
function PickerList({
  source,
  selectedUuids,
  onToggle,
  onPreview,
  selectionMode,
  folder,
  environment,
  storageBackend,
}: ListProps & { source: DatasetSource; folder: string }): ReactNode {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<readonly PickerItem[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<ListViewStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(
    async (nextPage: number) => {
      const requestId = ++requestRef.current;
      if (nextPage === 1) {
        setStatus("loading");
        setError(null);
      } else {
        setLoadingMore(true);
      }
      try {
        const resp = await listPage(source, {
          page: nextPage,
          size: PAGE_SIZE,
          search: search || undefined,
          ...(folder === ALL_FOLDERS_VALUE ? {} : { folder }),
          ...(environment === undefined ? {} : { environment }),
          ...(storageBackend === undefined ? {} : { storage_backend: storageBackend }),
        });
        if (requestId !== requestRef.current) {
          return;
        }
        const mapped = resp.items.map(toPickerItem);
        setTotal(resp.total);
        setItems((prev) => (nextPage === 1 ? mapped : [...prev, ...mapped]));
        setPage(nextPage);
        setStatus("success");
      } catch (caught) {
        if (requestId !== requestRef.current) {
          return;
        }
        setStatus("error");
        setError(resolveLoadErrorMessage(caught));
      } finally {
        if (requestId === requestRef.current) {
          setLoadingMore(false);
        }
      }
    },
    [source, search, folder, environment, storageBackend],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  // 어느 모드에서도 자동 선택하지 않는다. 사용자가 담을 항목을 직접 고른다.

  return (
    <>
      <div className={styles.pickerSearchRow}>
        <TextInput
          type="search"
          value={searchInput}
          placeholder="Dataset 이름을 검색해 보세요."
          onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchInput(event.target.value)}
        />
      </div>
      <MultiSelectList
        className={styles.pickerList}
        items={items}
        getKey={(item) => item.uuid}
        selectedKeys={selectedUuids}
        onToggle={(item) =>
          onToggle({
            uuid: item.uuid,
            label: item.name,
            // 담은 목록 카드가 이 값을 그대로 쓴다. 목록 응답으로 이미 만든 값이므로 추가 요청이 없다.
            preview: {
              thumbnailUrl: item.thumbnailUrl,
              meta: [item.meta.arms, item.meta.cameras, item.meta.episodes],
            },
          })
        }
        selectionMode={selectionMode}
        renderItem={renderPickerItem}
        onActiveItemChange={(item) => onPreview?.(item)}
        status={status}
        errorMessage={error}
        onRetry={() => void load(1)}
        emptyMessage={
          search !== ""
            ? NO_MATCH_MESSAGE
            : storageBackend === undefined
              ? SOURCE_COPY[source].emptyMessage
              : NARROWED_EMPTY_MESSAGE[source]
        }
        hasMore={items.length < total}
        loadingMore={loadingMore}
        onLoadMore={() => void load(page + 1)}
        ariaLabel={SOURCE_COPY[source].ariaLabel}
      />
    </>
  );
}

// 공용 Dataset 목록 행을 picker 항목으로 바꾼다. 파생 산출 중인 데이터셋도 담을 수 있다.
// 진단 산출물은 학습 입력이 아니므로 진단이 끝날 때까지 기다릴 필요가 없다.
function toPickerItem(dataset: Dataset): PickerItem {
  return {
    uuid: dataset.uuid,
    name: dataset.display_name,
    thumbnailUrl: dataset.thumbnail_url,
    meta: pickerItemMeta(dataset),
  };
}

// 이름 아래 요약 세 축. 팔 구성 · 카메라 수 · 에피소드 수다. 사용자는 이 값으로 이 데이터셋으로
// 학습할지 정한다. 세 값은 목록 응답에 이미 있으므로 행마다 추가 요청이 없다.
// embodiment 이름은 넣지 않는다. 폭을 모두 차지하고, 로봇 정보는 미리보기 패널이 표시한다.
function pickerItemMeta(dataset: Dataset): PickerItemMeta {
  return {
    arms: formatArmLayout(dataset.arm_layout) ?? "미판정",
    cameras: `카메라 ${formatNumber(dataset.camera_stream_count)}`,
    episodes: `에피소드 ${formatNumber(dataset.total_episodes)}`,
  };
}

function renderPickerItem(item: PickerItem): ReactNode {
  return (
    <div className={styles.pickerItem}>
      {/* 목록 표·카드와 같은 대표 섬네일(없으면 이니셜). 장식이므로 이름은 아래 텍스트가 제공한다. */}
      <DatasetPreview label={item.name} imageUrl={item.thumbnailUrl} mode="table" />
      <div className={styles.pickerItemText}>
        <div className={styles.pickerItemHead}>
          <Text variant={truncatedBody} className={styles.pickerItemName}>
            {item.name}
          </Text>
        </div>
        {/* 세 축은 잘리지 않는다. 트랙 폭이 내용 폭이므로 행이 달라도 열이 세로로 정렬된다. */}
        <div className={styles.pickerItemMeta}>
          <Text variant={truncatedCaption}>{item.meta.arms}</Text>
          <Text variant={truncatedCaption}>{item.meta.cameras}</Text>
          <Text variant={truncatedCaption}>{item.meta.episodes}</Text>
        </div>
      </div>
    </div>
  );
}
